import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, PLATFORM_ID, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Location } from './../../models/location.model';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SheetService } from '../../../services/sheet.service';
import { PropertyService } from '../../../../features/property/services/property.service';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Router } from '@angular/router';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { environment } from '../../../../../environments/environment';
import { HapticsService } from '../../services/haptics.service';
import { Property } from '../../../../features/property/models/property.model';
import { SaveButtonComponent } from '../../../../features/property/components/save-button/save-button';
import { LoaderComponent } from '../loader/loader.component';

interface PropertyPreview extends Location {
  id: string;
  displayName?: string;
  city?: string;
  state?: string;
  name: string;
  lat: number;
  lng: number;
  imageUrl: string;
  linkUrl: string;
  property: Property;
}

@Component({
  selector: 'app-google-map-full',
  imports: [
    CommonModule,
    GoogleMap,
    GoogleMapsModule,
    MatButtonModule,
    MatProgressSpinner,
    SaveButtonComponent,
    LoaderComponent
],
  templateUrl: './google-map-full.html',
  styleUrl: './google-map-full.scss',
})
export class GoogleMapFullComponent implements OnInit, AfterViewInit, OnChanges  {
  private propertyService = inject(PropertyService);
  private sheet = inject(SheetService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private haptics = inject(HapticsService);

  private platformId = inject(PLATFORM_ID);
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  @ViewChildren(MapMarker) markerElements!: QueryList<MapMarker>;
  @Input() locations: Location[] = [];
  @Input() zoom = 10;
  @Input() height = '300px';
  @Input() selectedProperty: string | undefined = undefined;

  @Output() windowClosed = new EventEmitter<void>();
  public mapOptions!: google.maps.MapOptions;
  isMapLoading = true;
  initialCenter: google.maps.LatLngLiteral = { lat: 27.3364, lng: -82.5307 };
  isMapInitialized = false;
  selectedMarker: PropertyPreview | null = null;

  get infoWindowOptions(): google.maps.InfoWindowOptions {
    return {
      maxWidth:250,
    };
  }

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeMapOptions();

      // 1. Configure the options globally once
      setOptions({
        key: environment.googleMapsKey,
        v: 'weekly'
      });

      try {
        // 2. Use the top-level importLibrary function directly
        await importLibrary('maps');
        
        // 3. Success! Flip the switch
        this.isMapLoading = false;
        
        // 4. Trigger change detection since we're in an async block
        this.cdr.detectChanges(); 
      } catch (e) {
        //console.error("Maps failed to load", e);
        // Handle error state here so the UI doesn't hang on "Loading..."
        this.isMapLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  markers: Location[] = [];

  infoWindowContent = '';
  infoWindowPosition: google.maps.LatLngLiteral | null = null;

  ngAfterViewInit() {
    if (this.locations.length > 0) {
      this.fitMarkersToView();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['locations']) {
      if (this.locations.length > 0) {
        this.markers = this.locations;
        if (this.map) {
          this.fitMarkersToView();
        } else {
          // If map isn't ready, set the initialCenter for the first load
          this.initialCenter = this.getMarkersCenter();
        }
        // if (this.map) {
        //   this.fitMarkersToView();
        // }
        //console.log('google map locations after change', this.locations, this.markers);
      }
    }
    if (changes['selectedProperty']) {
      this.selectedProperty = changes['selectedProperty'].currentValue;
      this.infoWindow?.close();
      if (this.selectedProperty !== undefined) {
        setTimeout(() => this.openMarkerForSelected(), 5);
      }
    }
  }

  trackByLocationId(index: number, location: Location) {
    return location.id;
  }

  private initializeMapOptions() {
    this.mapOptions = {
      // DO NOT put 'center' here if you want it to be dynamic but not sticky
      zoom: 12,
      zoomControl: true,
      disableDefaultUI: true,
      gestureHandling: 'greedy',
      scrollwheel: true,
      styles: [ 
        // 🏔️ LAND - Primary Blue Shades
        { elementType: 'geometry', stylers: [{ color: '#8599c0' }] },        // a30
        { elementType: 'geometry.fill', stylers: [{ color: '#b5c1d9' }] },   // a20
        
        // 🌊 WATER - Primary Green Shades  
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#8dbfce' }] },     // a30
        { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#5ba6bb' }] }, // a40

        // 🛣️ ROADS - Primary Blue + Orange Accents
        { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#9dadcc' }] },      // a25
        { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#8e4b0d' }] }, // Orange
        { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#0d508e' }] }, // Primary Blue
        { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#8e4b0d' }] }, // Orange

        // 🏙️ LABELS - Clean Blue/Green
        { elementType: 'labels.text.fill', stylers: [{ color: '#5373a7' }] },    // a40
        { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff', weight: 0.01 }] },  // White for contrast

        // 🌳 PARKS - Light Green
        { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#a5cbd8', visibility: 'off' }] },   // a25
        { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#3d99b1' }] }, // a45

        // 🏪 POINTS OF INTEREST - Subtle Blue
        //{ featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6d86b3', visibility: 'off'  }] }, // a35
        {
          featureType: "poi",
          stylers: [{ visibility: "off" }]
        },
        // 🏘️ LOCALITY NAMES - Prominent Blue
        { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#37619a' }] }, // a45
        { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff', weight: 0.01 }] },
       ]
    };
  }

  setInitialLocation(lat: number, lng: number) {
    if (!this.isMapInitialized) {
      this.initialCenter = { lat, lng };
      this.isMapInitialized = true;
    }
  }

  private openMarkerForSelected() {
    if (!this.selectedProperty || !this.markerElements) return;

    const markerArray = this.markerElements.toArray();
    const index = this.markers.findIndex(m => m.id === this.selectedProperty);
    if (index !== -1 && markerArray[index]) {
      const targetData = this.markers[index];
      const targetMarkerComponent = markerArray[index];
      this.selectedMarker = targetData as PropertyPreview;
      const pos = { lat: targetData.lat, lng: targetData.lng };
      this.map.googleMap?.panTo(pos);
      this.infoWindow.open(targetMarkerComponent);
      this.cdr.detectChanges();
    }
  }

  onMarkerClick(markerData: Location) {
    //console.log('onMarkerClick');
    this.haptics.click();
    this.selectedProperty = markerData.id ?? undefined;
    this.openMarkerForSelected();
  }

  private fitMarkersToView() {
    //console.log('fit markers to view');
    // Safety check
    if (!this.map?.googleMap || this.markers.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    this.markers.forEach(marker => {
      bounds.extend({ lat: marker.lat, lng: marker.lng });
    });
    //console.log('bounds', bounds);
    // Fit bounds handles both centering and zooming automatically
    this.map.googleMap.fitBounds(bounds);

    // Optional: Avoid being too zoomed in if there is only 1 marker
    if (this.markers.length === 1) {
      this.map.googleMap.setZoom(14); 
    }
  }

  private getMarkersCenter(): google.maps.LatLngLiteral {
    if (this.markers.length === 0) return { lat: 40.7128, lng: -74.0060 };
    
    // Sum all lat/lng
    const latSum = this.markers.reduce((sum, marker) => sum + marker.lat, 0);
    const lngSum = this.markers.reduce((sum, marker) => sum + marker.lng, 0);
    
    // Average = CENTER!
    const centerLat = latSum / this.markers.length;
    const centerLng = lngSum / this.markers.length;

    return { lat: centerLat, lng: centerLng };
  }

  closeSheet() {
    this.haptics.click();
    this.selectedMarker = null;
    this.map.panTo(this.getMarkersCenter());
    this.cdr.detectChanges();
  }

  clearMarkers() {
    this.markers = [];
  }

  // Map options
  // get mapOptions(): google.maps.MapOptions {
  //   return {
  //     center: this.getMarkersCenter(),
  //     zoom: 12,
  //     zoomControl: true,
  //     disableDefaultUI: true,
  //     gestureHandling: 'greedy',
  //     scrollwheel: true,
  //     mapTypeControl: false,
  //     streetViewControl: false,
  //     styles: [ 
  //       // 🏔️ LAND - Primary Blue Shades
  //       { elementType: 'geometry', stylers: [{ color: '#8599c0' }] },        // a30
  //       { elementType: 'geometry.fill', stylers: [{ color: '#b5c1d9' }] },   // a20
        
  //       // 🌊 WATER - Primary Green Shades  
  //       { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#8dbfce' }] },     // a30
  //       { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#5ba6bb' }] }, // a40

  //       // 🛣️ ROADS - Primary Blue + Orange Accents
  //       { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#9dadcc' }] },      // a25
  //       { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#8e4b0d' }] }, // Orange
  //       { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#0d508e' }] }, // Primary Blue
  //       { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#8e4b0d' }] }, // Orange

  //       // 🏙️ LABELS - Clean Blue/Green
  //       { elementType: 'labels.text.fill', stylers: [{ color: '#5373a7' }] },    // a40
  //       { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff', weight: 0.01 }] },  // White for contrast

  //       // 🌳 PARKS - Light Green
  //       { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#a5cbd8', visibility: 'off' }] },   // a25
  //       { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#3d99b1' }] }, // a45

  //       // 🏪 POINTS OF INTEREST - Subtle Blue
  //       //{ featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6d86b3', visibility: 'off'  }] }, // a35
  //       {
  //         featureType: "poi",
  //         stylers: [{ visibility: "off" }]
  //       },
  //       // 🏘️ LOCALITY NAMES - Prominent Blue
  //       { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#37619a' }] }, // a45
  //       { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff', weight: 0.01 }] },
  //       // 🔥 MAP RED ACCENTS - For important features
  //       //{ featureType: 'poi.attraction', elementType: 'labels.text.fill', stylers: [{ color: '#ff5d5c', visibility: 'off' }] } // Map Red
  //     ]
  //   };
  // }

  // Marker options
  getMarkerOptions(index: number): google.maps.MarkerOptions {
    const colors = [
      '#ff5d5c',    // Map Red
      '#0d508e',    // Primary Blue  
      '#0d8e8c',    // Primary Green
      '#8e4b0d'     // Primary Orange
    ];
    
    return {
      // icon: {
      //   //path: google?.maps?.SymbolPath.CIRCLE || 0,
      //   // path: 0,
      //   scale: 10,
      //   // fillColor: colors[index % colors.length],
      //   // fillOpacity: 0.9,
      //   strokeWeight: 2,
      //   strokeColor: '#ffffff'
      // }
    };
  }

  openProperty(id: string) {
    if (id) {
      this.haptics.click();
      this.propertyService.getPropertyById(id).subscribe(property => {
        this.router.navigate(['/property', property?.id])
          .then(success => console.log('Navigation success:', success))
          .catch(err => console.error('Navigation error:', err));
      });
      // this.propertyService.getPropertyById(id).subscribe(property => {
      //   this.sheet.open(PropertyView, { inputProperty: property });
      // });
    }
  }

  closeInfoWindow() {
    this.haptics.click();
    this.infoWindow.close();
    this.selectedProperty = undefined;
    this.windowClosed.emit();
  }

  // onMapReady() {
  //   console.log('onMapReady');
  //   this.isMapLoading = false;
  //   setTimeout(() => { 
  //     this.cdr.detectChanges();
  //   }, 5);
  // }

  onMapReady() {
    //console.log('Map Tiles Loaded');
    // Wrapping in setTimeout ensures this runs in the next tick 
    // and triggers Angular Change Detection
    setTimeout(() => {
      this.isMapLoading = false;
      this.cdr.detectChanges(); 
    }, 100);
  }
}
